/**
 * TPM Screen-based State Machine Reducer
 *
 * Screens:
 * - MANAGE_ACCOUNTS: Show existing accounts table
 * - NUMBER_INPUT: Phone number input form
 * - LOADING: 15-minute wait for operator validation
 * - VERIFICATION: 6-digit MFA code entry
 * - ERROR: Error display with retry options
 */

/** @typedef {'MANAGE_ACCOUNTS' | 'NUMBER_INPUT' | 'LOADING' | 'VERIFICATION' | 'ERROR'} TpmScreen */

/**
 * @typedef {Object} TpmState
 * @property {TpmScreen} screen - Current screen
 * @property {string} phoneNumber - Phone number being processed
 * @property {string} verificationCode - 6-digit verification code
 * @property {boolean} isSubmitting - Loading state for API calls
 * @property {string|null} error - Error message
 * @property {number|null} requestStartTime - Timestamp when validation started
 * @property {number|null} lastCodeSentTime - Timestamp when code was last sent
 * @property {Array|null} countries - Countries data for phone input
 */

export const initialState = {
    screen: 'MANAGE_ACCOUNTS',
    phoneNumber: '',
    verificationCode: '',
    isSubmitting: false,
    error: null,
    requestStartTime: null,
    lastCodeSentTime: null,
    countries: null,
};

// Timing constants
export const REQUEST_TIMEOUT = 15 * 60 * 1000; // 15 minutes
export const CODE_EXPIRY = 2 * 60 * 1000; // 2 minutes for SMS code
export const SESSION_EXPIRED_TIME = 30 * 60 * 1000; // 30 minute session timeout

// Action types
export const TPM_ACTIONS = {
    GO_TO_SCREEN: 'GO_TO_SCREEN',
    SET_PHONE_NUMBER: 'SET_PHONE_NUMBER',
    START_VALIDATION: 'START_VALIDATION',
    VALIDATION_SUCCESS: 'VALIDATION_SUCCESS',
    VALIDATION_FAILURE: 'VALIDATION_FAILURE',
    SET_VERIFICATION_CODE: 'SET_VERIFICATION_CODE',
    SUBMIT_CODE_START: 'SUBMIT_CODE_START',
    SUBMIT_CODE_SUCCESS: 'SUBMIT_CODE_SUCCESS',
    SUBMIT_CODE_FAILURE: 'SUBMIT_CODE_FAILURE',
    RESEND_CODE: 'RESEND_CODE',
    CLEAR_ERROR: 'CLEAR_ERROR',
    SET_COUNTRIES: 'SET_COUNTRIES',
    RESTORE_STATE: 'RESTORE_STATE',
    RESET: 'RESET',
};

/**
 * TPM Reducer - Screen-based state machine
 * @param {TpmState} state
 * @param {{ type: string, payload?: any }} action
 * @returns {TpmState}
 */
export function tpmReducer(state, action) {
    const { type, payload } = action;

    switch (type) {
        // Navigation
        case TPM_ACTIONS.GO_TO_SCREEN: {
            return {
                ...state,
                screen: payload,
                error: null,
            };
        }

        // Phone number input
        case TPM_ACTIONS.SET_PHONE_NUMBER: {
            return {
                ...state,
                phoneNumber: payload,
                error: null,
            };
        }

        // STEP 1: Start number validation request
        case TPM_ACTIONS.START_VALIDATION: {
            return {
                ...state,
                screen: 'LOADING',
                phoneNumber: payload?.phoneNumber || state.phoneNumber,
                isSubmitting: true,
                requestStartTime: Date.now(),
                error: null,
            };
        }

        // STEP 1 success: Operator found the number
        case TPM_ACTIONS.VALIDATION_SUCCESS: {
            return {
                ...state,
                screen: 'VERIFICATION',
                isSubmitting: false,
                lastCodeSentTime: Date.now(),
                error: null,
            };
        }

        // STEP 1 failure
        case TPM_ACTIONS.VALIDATION_FAILURE: {
            return {
                ...state,
                screen: 'ERROR',
                isSubmitting: false,
                error: payload || 'Request failed. Please try again.',
            };
        }

        // STEP 2: Verification code input
        case TPM_ACTIONS.SET_VERIFICATION_CODE: {
            return {
                ...state,
                verificationCode: payload,
            };
        }

        // STEP 2: Submit verification code
        case TPM_ACTIONS.SUBMIT_CODE_START: {
            return {
                ...state,
                isSubmitting: true,
                error: null,
            };
        }

        // STEP 2 success: Code verified
        case TPM_ACTIONS.SUBMIT_CODE_SUCCESS: {
            // Reset to initial but keep countries
            return {
                ...initialState,
                countries: state.countries,
            };
        }

        // STEP 2 failure: Wrong code
        case TPM_ACTIONS.SUBMIT_CODE_FAILURE: {
            return {
                ...state,
                isSubmitting: false,
                verificationCode: '',
                error: payload || 'Invalid verification code.',
            };
        }

        // Resend verification code
        case TPM_ACTIONS.RESEND_CODE: {
            return {
                ...state,
                verificationCode: '',
                lastCodeSentTime: Date.now(),
                error: null,
            };
        }

        // Clear error and return to input
        case TPM_ACTIONS.CLEAR_ERROR: {
            return {
                ...state,
                screen: 'NUMBER_INPUT',
                error: null,
                verificationCode: '',
            };
        }

        // Set countries data
        case TPM_ACTIONS.SET_COUNTRIES: {
            return {
                ...state,
                countries: payload,
            };
        }

        // Restore state from Redux (for resume)
        case TPM_ACTIONS.RESTORE_STATE: {
            if (!payload) return state;
            return {
                ...state,
                screen: payload.screen || state.screen,
                phoneNumber: payload.phoneNumber || state.phoneNumber,
                requestStartTime: payload.requestStartTime || null,
                lastCodeSentTime: payload.lastCodeSentTime || null,
            };
        }

        // Full reset
        case TPM_ACTIONS.RESET: {
            return {
                ...initialState,
                countries: state.countries,
            };
        }

        default:
            return state;
    }
}
